import {
  AdminOrganization,
  AdminUser,
} from "../../utils/types";

type LoginBucketKey = "24h" | "7d" | "30d" | "inactive";

type LoginBuckets = Record<LoginBucketKey, number>;

type OrgDistributionItem = {
  orgId: number | null;
  orgName: string;
  count: number;
};

export type AdminStatsSummary = {
  totalUsers: number;
  uploadedUsers: number;
  linkedUsers: number;
  unlinkedUsers: number;
  loginBuckets: LoginBuckets;
  orgDistribution: OrgDistributionItem[];
  uploadAdoptionPct: number;
  recentActivity: Array<{
    id: number;
    username: string;
    email: string;
    lastLoggedIn: string | null;
  }>;
  recentSignups: Array<{
    id: number;
    username: string;
    email: string;
    createdAt: string | null;
  }>;
};

const EMPTY_LOGIN_BUCKETS: LoginBuckets = {
  "24h": 0,
  "7d": 0,
  "30d": 0,
  inactive: 0,
};

export const toMs = (dateStr?: string | null): number | null => {
  if (!dateStr) return null;
  const parsed = Date.parse(dateStr);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveOrgName = (
  orgId: number | null,
  orgNameById: Map<number, string>,
): string => {
  if (orgId === null) return "Unlinked";
  return orgNameById.get(orgId) ?? `Organization #${orgId}`;
};

export const buildAdminStats = (
  users: AdminUser[],
  organizations: AdminOrganization[],
): AdminStatsSummary => {
  const nowMs = Date.now();
  const loginBuckets: LoginBuckets = { ...EMPTY_LOGIN_BUCKETS };
  const orgCounts = new Map<number | null, number>();
  const orgNameById = new Map(organizations.map((org) => [org.id, org.name]));

  let uploadedUsers = 0;
  let linkedUsers = 0;

  for (const user of users) {
    const loginMs = toMs(user.last_logged_in);
    if (loginMs === null) {
      loginBuckets.inactive += 1;
    } else {
      const ageMs = nowMs - loginMs;
      if (ageMs <= 24 * 60 * 60 * 1000) loginBuckets["24h"] += 1;
      else if (ageMs <= 7 * 24 * 60 * 60 * 1000) loginBuckets["7d"] += 1;
      else if (ageMs <= 30 * 24 * 60 * 60 * 1000) loginBuckets["30d"] += 1;
      else loginBuckets.inactive += 1;
    }

    if (user.has_uploaded_file) uploadedUsers += 1;
    if ((user.budy_organization_ids?.length ?? 0) > 0 || user.budy_organization_id !== null) {
      linkedUsers += 1;
    }

    const userOrgIds = user.budy_organization_ids?.length
      ? user.budy_organization_ids
      : user.budy_organization_id !== null
        ? [user.budy_organization_id]
        : [];

    if (userOrgIds.length === 0) {
      orgCounts.set(null, (orgCounts.get(null) ?? 0) + 1);
    } else {
      for (const orgId of userOrgIds) {
        orgCounts.set(orgId, (orgCounts.get(orgId) ?? 0) + 1);
      }
    }
  }

  const totalUsers = users.length;
  const orgIds = new Set(organizations.map((org) => org.id));
  const orgDistribution = Array.from(orgCounts.entries())
    .filter(([orgId]) => orgId === null || orgIds.has(orgId))
    .map(([orgId, count]) => ({
      orgId,
      orgName: resolveOrgName(orgId, orgNameById),
      count,
    }))
    .sort((a, b) => b.count - a.count);

  const recentActivity = [...users]
    .sort(
      (a, b) => (toMs(b.last_logged_in) ?? 0) - (toMs(a.last_logged_in) ?? 0),
    )
    .slice(0, 6)
    .map((user) => ({
      id: user.id,
      username: user.username,
      email: user.email,
      lastLoggedIn: user.last_logged_in ?? null,
    }));

  const recentSignups = [...users]
    .sort((a, b) => (toMs(b.created_at) ?? 0) - (toMs(a.created_at) ?? 0))
    .slice(0, 6)
    .map((user) => ({
      id: user.id,
      username: user.username,
      email: user.email,
      createdAt: user.created_at ?? null,
    }));

  return {
    totalUsers,
    uploadedUsers,
    linkedUsers,
    unlinkedUsers: totalUsers - linkedUsers,
    loginBuckets,
    orgDistribution,
    uploadAdoptionPct:
      totalUsers === 0 ? 0 : Math.round((uploadedUsers / totalUsers) * 100),
    recentActivity,
    recentSignups,
  };
};
